import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import '../../../Common/ShimmerLoader.css';
import { getOrderItems, getReturns, markOrdersReadyForPickup, markOrdersPickedUp } from '../../../api/shop/orders/orderService';
import { OrderItem, OrderFilters, OrderStatus } from '../../../api/shop/types/orderTypes';
import { STANDARD_PAGE_SIZES, VALID_TABS, SELECTABLE_STATUSES } from './constants';
import OrderCard from './components/OrderCard';
import Pagination from './components/Pagination';
import { MarkReadyModal } from './components/MarkReadyModal';
import './OrdersPage.css';

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize status filter from URL params or default to 'All'
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'All'>(() => {
    const tabFromUrl = searchParams.get('tab');
    return VALID_TABS.includes(tabFromUrl as any) ? (tabFromUrl as OrderStatus | 'All') : 'All';
  });
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<any>({});
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Selection state for bulk actions (CONFIRMED and READY_FOR_PICKUP orders)
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [showMarkReadyModal, setShowMarkReadyModal] = useState(false);
  const [isMarkingReady, setIsMarkingReady] = useState(false);
  
  // AbortController ref for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchOrders = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setLoading(true);
      setError(null);
      
      // Use returns API for RETURN tab, otherwise use regular orders API
      if (statusFilter === 'RETURN') {
        const response = await getReturns(page, pageSize);
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }
        
        setOrders(response.items);
        // Don't update statusCounts for returns (preserve existing counts)
        setTotalCount(response.totalCount);
        
        // Calculate totalPages using current pageSize
        setTotalPages(Math.ceil(response.totalCount / pageSize));
      } else {
        // When date filters are applied, always use page 1 for the first request
        const currentPage = (startDate || endDate) && page > 1 ? 1 : page;
        
        const filters: OrderFilters = {
          pageNum: currentPage,
          pageSize: pageSize,
          status: statusFilter,
        };

        if (startDate) {
          filters.startAt = startDate;
        }
        if (endDate) {
          filters.endAt = endDate;
        }

        const response = await getOrderItems(filters);
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }
        
        setOrders(response.items);
        setStatusCounts(response.statusCounts);
        // Use the totalCount from API response (should be filtered count)
        setTotalCount(response.totalCount || 0);
        setTotalPages(Math.ceil((response.totalCount || 0) / pageSize));
        
        // If we forced page to 1, update the page state
        if (currentPage === 1 && page > 1) {
          setPage(1);
        }
      }
    } catch (err: any) {
      // Don't set error if request was aborted (user changed filters or component unmounted)
      if (abortController.signal.aborted || err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return;
      }
      setError(err.message || 'Failed to load orders');
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [page, statusFilter, startDate, endDate, pageSize]);

  // Reset page to 1 when date filters change
  useEffect(() => {
    if (startDate || endDate) {
      setPage(1);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchOrders();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchOrders]);

  // Validate and adjust page number if it exceeds totalPages
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(1); // Reset to page 1 if current page exceeds total pages
    }
  }, [totalPages, page]);

  // Clear selections when status filter changes
  useEffect(() => {
    setSelectedOrders([]);
  }, [statusFilter]);

  const handleStatusFilterChange = (status: OrderStatus | 'All') => {
    setStatusFilter(status);
    setPage(1); // Reset to first page when changing filters
    setPageSize(10); // Reset to default page size when switching tabs
    
    // Reset date filters when switching tabs
    setStartDate('');
    setEndDate('');
    
    // Update URL search params to maintain tab state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', status);
    setSearchParams(newSearchParams, { replace: true });
  };


  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    // If end date is before new start date, clear it
    if (endDate && value && endDate < value) {
      setEndDate('');
    }
    // Reset to page 1 when date filter changes
    setPage(1);
  };

  const handleEndDateChange = (value: string) => {
    // Validate that end date is not before start date
    if (startDate && value && value < startDate) {
      // Show validation error - don't update end date
      return;
    }
    setEndDate(value);
    // Reset to page 1 when date filter changes
    setPage(1);
  };
  
  // Reset page to 1 when date filters change
  useEffect(() => {
    if (startDate || endDate) {
      setPage(1);
    }
  }, [startDate, endDate]);

  // Selection handlers for bulk actions
  const handleOrderSelection = (orderItemId: string, isSelected: boolean) => {
    setSelectedOrders(prev => 
      isSelected 
        ? [...prev, orderItemId]
        : prev.filter(id => id !== orderItemId)
    );
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      // Select orders based on current tab
      const selectableOrderIds = orders
        .filter(order => {
          // Only allow selection for CONFIRMED and READY_FOR_PICKUP tabs
          return SELECTABLE_STATUSES.includes(statusFilter as any) && order.status === statusFilter;
        })
        .map(order => order.orderItemId);
      setSelectedOrders(selectableOrderIds);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleClearSelections = () => {
    setSelectedOrders([]);
  };

  const handleMarkAsReady = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Prevent double-opening by checking if modal is already showing
    if (!showMarkReadyModal) {
      setShowMarkReadyModal(true);
    }
  };

  const handleConfirmMarkReady = async () => {
    try {
      setIsMarkingReady(true);
      
      // Choose API based on current status filter
      if (statusFilter === 'CONFIRMED') {
        await markOrdersReadyForPickup(selectedOrders);
      } else if (statusFilter === 'READY_FOR_PICKUP') {
        await markOrdersPickedUp(selectedOrders);
      }
      
      // Success - refresh orders and clear selections
      setSelectedOrders([]);
      setShowMarkReadyModal(false);
      await fetchOrders(); // Refresh the orders list
    } catch (error) {
      // Error handled silently
    } finally {
      setIsMarkingReady(false);
    }
  };

  const handleCloseMarkReadyModal = () => {
    if (!isMarkingReady) {
      setShowMarkReadyModal(false);
    }
  };



  const statusTabs = [
    { key: 'All', label: 'All', count: totalCount },
    { key: 'CONFIRMED', label: 'Confirmed', count: statusCounts.CONFIRMED || 0 },
    { key: 'READY_FOR_PICKUP', label: 'Ready for pickup', count: statusCounts.READY_FOR_PICKUP || 0 },
    { key: 'DELIVERED', label: 'Delivered', count: statusCounts.DELIVERED || 0 },
    { key: 'CANCELLED', label: 'Canceled', count: statusCounts.CANCELLED || 0 },
    { key: 'RETURN', label: 'Returns', count: statusCounts.RETURN || 0 },
    // { key: 'REPLACEMENT', label: 'Replacements', count: statusCounts.REPLACEMENT || 0 }, // Hidden for now
  ];

  if (error) {
    return (
      <div className="page-content orders-page" style={{ marginTop: '20px' }}>
        <Container fluid>
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content orders-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
            { label: 'Orders', active: true }
          ]} />
        </div>

        {/* 1. Header Section - Page Title and Back Button */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="orders-page-header">
            <button 
              className="back-button"
              onClick={() => navigate('/shop-services/sellers')}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="page-title mb-0">Orders</h1>
          </div>
        </div>

        {/* 2. Tab Section - Status Filter Tabs */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="orders-status-tabs-container">
            <div className="orders-status-tabs">
              {statusTabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`orders-status-tab ${statusFilter === tab.key ? 'active' : ''}`}
                  onClick={() => handleStatusFilterChange(tab.key as OrderStatus | 'All')}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 3. Filter and Cards Section */}
        <div style={{ padding: '0 15px' }}>
          <div className="orders-content-container">
            {/* Filters and Count Header */}
            <div className="orders-filters-header">
              <div className="orders-count">
                {(statusFilter === 'CONFIRMED' || statusFilter === 'READY_FOR_PICKUP') && orders.length > 0 ? (
                  <div className="orders-count-with-checkbox">
                    <Form.Check
                      type="checkbox"
                      id="select-all-orders"
                      checked={(() => {
                        const selectableOrders = orders.filter(o => o.status === statusFilter);
                        return selectedOrders.length === selectableOrders.length && selectableOrders.length > 0;
                      })()}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="orders-select-all-checkbox"
                    />
                    <h3>{totalCount} Orders</h3>
                  </div>
                ) : (
                  <h3>{totalCount} Orders</h3>
                )}
              </div>
              
              {/* Hide filters for RETURN tab as returns API doesn't support them */}
              {statusFilter !== 'RETURN' && (
                <div className="orders-filters">
                  <div className="orders-filter-item">
                    <label>Start Date</label>
                    <div className="orders-date-range">
                      <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        max={endDate || undefined}
                        className="orders-date-input"
                      />
                    </div>
                  </div>
                  
                  <div className="orders-filter-item">
                    <label>End Date</label>
                    <div className="orders-date-range">
                      <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(e) => handleEndDateChange(e.target.value)}
                        min={startDate || undefined}
                        className="orders-date-input"
                        style={{
                          borderColor: startDate && endDate && endDate < startDate ? '#DC2626' : undefined
                        }}
                      />
                      {startDate && endDate && endDate < startDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                          End date must be after start date
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Orders Grid */}
          {loading ? (
            <div className="orders-loading">
              <ShimmerLoader type="tournament-card" rows={6} />
            </div>
          ) : orders.length === 0 ? (
            <div className="orders-empty">
              <div className="text-center py-5">
                <i className="ri-shopping-bag-line" style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }}></i>
                <h4 className="text-muted">No orders found</h4>
                <p className="text-muted">There are no orders matching your current filters.</p>
              </div>
            </div>
          ) : (
            <Row>
              {orders.map((order) => (
                <Col key={order.orderItemId} xl={4} lg={6} md={6} sm={12} className="mb-4">
                  <OrderCard 
                    order={order}
                    onViewOrder={(orderId, orderItemId, orderType) => {
                      const isCancelled = orderType === 'CANCELLED' || 
                                         order.status === 'CANCELLED' ||
                                         order.status?.toUpperCase() === 'CANCELLED';
                      
                      if (isCancelled) {
                        navigate(`/shop-services/cancellations/${orderId}/${orderItemId}`);
                        return;
                      }
                      
                      if (!orderItemId && orderId) {
                        const returnId = order.returnRequest?.returnId || order.returnId || orderId;
                        if (order.returnRequest || order.returnId || returnId) {
                          navigate(`/shop-services/returns/${returnId}`);
                          return;
                        }
                      }
                      
                      if (order.returnRequest) {
                        const returnId = order.returnRequest.returnId || order.returnId;
                        if (returnId) {
                          navigate(`/shop-services/returns/${returnId}`);
                          return;
                        }
                      }
                      
                      if (order.returnId) {
                        navigate(`/shop-services/returns/${order.returnId}`);
                        return;
                      }
                      
                      navigate(`/shop-services/orders/${orderId}/${orderItemId}`);
                    }}
                    showCheckbox={SELECTABLE_STATUSES.includes(statusFilter as any) && order.status === statusFilter}
                    isSelected={selectedOrders.includes(order.orderItemId)}
                    onSelectionChange={(isSelected) => handleOrderSelection(order.orderItemId, isSelected)}
                  />
                </Col>
              ))}
            </Row>
          )}
          </div>

          {/* Pagination - Outside orders-content-container to match Products page structure */}
          {!loading && totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              displayedCount={orders.length} // Pass actual number of items displayed
              onPageChange={setPage}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1); // Reset to first page when page size changes
              }}
            />
          )}
        </div>

        {/* Sticky Action Bar - Only show when orders are selected */}
        {selectedOrders.length > 0 && (
          <div className="orders-action-bar">
            <Container fluid>
              <div className="orders-action-bar-content">
                <Button
                  variant="outline-secondary"
                  className="orders-action-cancel"
                  onClick={handleClearSelections}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="orders-action-confirm"
                  onClick={(e) => handleMarkAsReady(e)}
                  disabled={showMarkReadyModal || isMarkingReady}
                >
                  {statusFilter === 'CONFIRMED' 
                    ? `Mark ${selectedOrders.length} Items to "Ready for pickup"`
                    : `Mark ${selectedOrders.length} Items as "Delivered"`
                  }
                </Button>
              </div>
            </Container>
          </div>
        )}

        {/* Mark Ready Confirmation Modal */}
        <MarkReadyModal
          show={showMarkReadyModal}
          handleClose={handleCloseMarkReadyModal}
          handleConfirm={handleConfirmMarkReady}
          orderCount={selectedOrders.length}
          isLoading={isMarkingReady}
          actionType={statusFilter === 'CONFIRMED' ? 'ready-for-pickup' : 'delivered'}
        />
      </Container>
    </div>
  );
};

export default OrdersPage;